import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { MARKET_INTELLIGENCE_DEMO as MOCK } from "@/lib/marketIntelligenceDemo";
import { formatPassengers, formatRevenue, formatFare, localizeLabel } from "@/lib/marketFormat";
import { DEMO_MAURITIUS_MI, filterDemoMauritiusCatalog, isMauritiusDemoOrg } from "@/lib/demoMauritius";

// Merges live Sabre ADI air metrics (when available) with the demo layer.
// Air metrics become "live"; tourism/macro/signals/news/monthly stay demo (fallback).
// Member-facing display alias for the data provider (internal source stays "Sabre ADI").
// Labelled as shared Lodge reference data (NOT the org's own imported data).
const MEMBER_SOURCE_LABEL = "Lodge Market Data";
export function useMarketIntelligence(marketKey) {
  const { activeOrgId, activeMembership } = useAuth();
  // Demo Maurice only: honest fallback (no illustrative values) + seven approved ADI market views.
  const mauritiusDemo = isMauritiusDemoOrg(activeMembership?.org);
  const BASE = mauritiusDemo ? DEMO_MAURITIUS_MI : MOCK;
  const [catalog, setCatalog] = useState({ orgId: null, markets: [] });
  const [dataScope, setDataScope] = useState(null);
  const [data, setData] = useState(MOCK);
  const [airLive, setAirLive] = useState(false);
  const [airLoading, setAirLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!activeOrgId) return;
    api.get("/market-data/markets", { headers: { "X-Organization-ID": activeOrgId } })
      .then(({ data }) => {
        if (cancelled) return;
        const markets = (data?.markets || []).map((m) => ({
          value: m.key, label: localizeLabel(m.origin, m.destination), origin: m.origin, destination: m.destination,
        }));
        setCatalog({ orgId: activeOrgId, markets: mauritiusDemo ? filterDemoMauritiusCatalog(markets) : markets });
      })
      .catch(() => { if (!cancelled) setCatalog({ orgId: activeOrgId, markets: [] }); });
    return () => { cancelled = true; };
  }, [activeOrgId, mauritiusDemo]);

  useEffect(() => {
    let cancelled = false;
    setAirLoading(true);
    setAirLive(false);
    if (!activeOrgId || !marketKey) return;
    api.get(`/market-data/air/${marketKey}`, { headers: { "X-Organization-ID": activeOrgId } })
      .then(({ data: adi }) => {
        if (cancelled) return;
        if (!adi?.kpis) {
          setAirLoading(false);
          setData({ ...BASE, period: { ...BASE.period, subLabel: BASE.period.label } });
          return;
        }
        setDataScope({ orgId: activeOrgId, marketKey });
        setAirLive(true);
        setAirLoading(false);
        const paxF = formatPassengers(adi.kpis.airPassengers.raw);
        const revF = formatRevenue(adi.kpis.airRevenue.raw);
        const fareF = formatFare(adi.kpis.averageFare.raw);
        setData({
          ...BASE,
          market: { key: adi.market.key, origin: adi.market.origin, destination: adi.market.destination, label: localizeLabel(adi.market.origin, adi.market.destination) },
          period: { label: adi.period.range_label, subLabel: adi.period.sub_label, months: BASE.period.months },
          kpis: {
            airPassengers: { ...adi.kpis.airPassengers, value: paxF.value, unit: paxF.unit, source: MEMBER_SOURCE_LABEL },
            airRevenue: { ...adi.kpis.airRevenue, value: revF.value, unit: revF.unit, source: MEMBER_SOURCE_LABEL },
            averageFare: { ...adi.kpis.averageFare, value: fareF.value, unit: fareF.unit, source: MEMBER_SOURCE_LABEL },
            internationalVisitors: BASE.kpis.internationalVisitors, // still demo (or unavailable for Demo Maurice)
          },
          topAirlinesByPassengers: adi.topAirlinesByPassengers,
          topAirlinesByRevenue: adi.topAirlinesByRevenue,
          airSource: MEMBER_SOURCE_LABEL, // member-facing alias for the data provider
        });
      })
      .catch(() => {
        if (cancelled) return;
        setAirLive(false);
        setAirLoading(false);
        setData({ ...BASE, period: { ...BASE.period, subLabel: BASE.period.label } });
      });
    return () => { cancelled = true; };
  }, [marketKey, activeOrgId, BASE]);

  // Do not render a previous organization's authorized response, even for one render.
  const current = dataScope?.orgId === activeOrgId && dataScope?.marketKey === marketKey;
  return {
    markets: catalog.orgId === activeOrgId ? catalog.markets : [],
    data: current ? data : BASE,
    airLive: current && airLive,
    airLoading,
  };
}
